import React, { useCallback, useEffect, useRef, useState } from 'react';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';

/**
 * Tracks a scrolling track element and reports which edges have content
 * scrolled out of view, for toggling the `jc-scroll-fade-start` /
 * `jc-scroll-fade-end` classes (see JcScrollFade.css).
 */
export function useJcScrollFade(
    ref: React.RefObject<HTMLElement | null>,
    vertical: boolean,
): { readonly fadeStart: boolean; readonly fadeEnd: boolean } {
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(true);

    const updateFades = useCallback(() => {
        const track = ref.current;
        if (!track) return;
        if (vertical) {
            const maxScroll = track.scrollHeight - track.clientHeight;
            setAtStart(track.scrollTop <= 1);
            setAtEnd(track.scrollTop >= maxScroll - 1);
            return;
        }
        const maxScroll = track.scrollWidth - track.clientWidth;
        setAtStart(track.scrollLeft <= 1);
        // <= 1px slack absorbs sub-pixel rounding so the end fade fully clears.
        setAtEnd(track.scrollLeft >= maxScroll - 1);
    }, [ref, vertical]);

    useEffect(() => {
        const track = ref.current;
        if (!track) return;
        updateFades();
        track.addEventListener('scroll', updateFades, { passive: true });
        const observer = new ResizeObserver(updateFades);
        observer.observe(track);
        return () => {
            track.removeEventListener('scroll', updateFades);
            observer.disconnect();
        };
    }, [ref, updateFades]);

    return { fadeStart: !atStart, fadeEnd: !atEnd };
}

export interface JcScrollFadeProps extends JcCommonProps {
    readonly children: React.ReactNode;
    /** Scroll axis and fade edges. Defaults to `'vertical'`. */
    readonly direction?: 'horizontal' | 'vertical';
}

/**
 * A scrollable region with a soft gradient fade at whichever edge still has
 * content scrolled out of view — hinting that the region scrolls and there is
 * more to see. Not a list — for a scrollable list of items use `JcScrollList`,
 * which shares this fade treatment. The fade color defaults to the app
 * background; override `--jc-scroll-fade-color` to match a tinted surface, or
 * the per-edge `--jc-scroll-fade-start-color` / `--jc-scroll-fade-end-color`
 * on a gradient surface.
 */
export const JcScrollFade = (props: JcScrollFadeProps): React.ReactElement => {
    const { direction = 'vertical' } = props;
    const vertical = direction === 'vertical';
    const commonProps = JcUtils.commonProps(props, ['jc-scroll-fade', vertical && 'jc-scroll-fade-vertical']);
    const trackRef = useRef<HTMLDivElement>(null);
    const { fadeStart, fadeEnd } = useJcScrollFade(trackRef, vertical);

    const className = JcUtils.classNameString([
        commonProps.className,
        fadeStart && 'jc-scroll-fade-start',
        fadeEnd && 'jc-scroll-fade-end',
    ]);

    return (
        <div {...commonProps} className={className}>
            <div className="jc-scroll-fade-track" ref={trackRef}>
                {props.children}
            </div>
        </div>
    );
};
